import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useArrayFilter } from '@vueuse/core';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import type {
  OwnProduct,
  Product,
  CreateProductRequest,
  EditProductRequest,
} from '@@/packages/shared/types/models/products';
import {
  OWN_PRODUCTS_QUERY,
  ORGANISATION_PRODUCTS_QUERY,
  SUPPLIER_ASSOCIATED_PRODUCT_IDS_QUERY,
  CLIENT_ASSOCIATED_PRODUCT_IDS_QUERY,
} from './queryKeys';

type SetSupplierAssociatedProductsRequest = {
  supplierID: string;
  productIDs: string[];
};

type SetClientAssociatedProductsRequest = {
  clientID: string;
  productIDs: string[];
};

export function useOwnProducts(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [OWN_PRODUCTS_QUERY],
      async queryFn() {
        const res = await request<OwnProduct[]>('GET', '/products');
        return res.data;
      },
    }),
  );

  const ownProducts = computed(
    () =>
      query.data.value
        ?.map<OwnProduct>((product) => ({
          ...product,
          answerIDs: product.answerIDs ?? [], // Work around backend issue where field can be null
          clientIDs: product.clientIDs ?? [],
        }))
        .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)) ?? [],
  );
  const archivedOwnProducts = useArrayFilter(ownProducts, (product) => product.archivedAt);
  const activeOwnProducts = useArrayFilter(ownProducts, (product) => !product.archivedAt);

  const productsByID = computed(() =>
    Object.fromEntries(ownProducts.value.map((product) => [product.id, product])),
  );

  return {
    ...query,
    query,
    ownProducts,
    archivedOwnProducts,
    activeOwnProducts,
    productsByID,
  };
}

export function useOrganisationProducts(
  organisationID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ORGANISATION_PRODUCTS_QUERY, toRef(organisationID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<Product[]>('GET', `/organisations/${queryKey[1]}/products`);
        return res.data;
      },
    }),
  );

  const products = computed(() => query.data.value ?? []);
  const archivedProducts = useArrayFilter(products, (product) => product.archivedAt);
  const activeProducts = useArrayFilter(products, (product) => !product.archivedAt);

  const productsByID = computed(() =>
    Object.fromEntries(products.value.map((product) => [product.id, product])),
  );

  return {
    ...query,
    products,
    archivedProducts,
    activeProducts,
    productsByID,
  };
}

export function useSupplierAssociatedProductIDs(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_ASSOCIATED_PRODUCT_IDS_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<string[]>('GET', `/suppliers/${queryKey[1]}/associated-products`);
        return res.data;
      },
    }),
  );

  const associatedProductIDs = computed(() => query.data?.value ?? []);

  return {
    associatedProductIDs,
    ...query,
  };
}

export function useClientAssociatedProductIDs(
  clientID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_ASSOCIATED_PRODUCT_IDS_QUERY, toRef(clientID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<string[]>('GET', `/clients/${queryKey[1]}/associated-products`);
        return res.data;
      },
    }),
  );

  const associatedProductIDs = computed(() => query.data?.value ?? []);

  return {
    associatedProductIDs,
    ...query,
  };
}

export function useCreateProduct(options?: MutationOptions<Product, CreateProductRequest>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(createProductRequest) {
        const res = await request<Product>('POST', '/products', createProductRequest);
        return res.data;
      },
      async onSuccess(data, variables) {
        queryClient.setQueryData([OWN_PRODUCTS_QUERY], (oldData: OwnProduct[] | undefined) => {
          if (!oldData) return undefined;
          return [
            { ...data, clientIDs: variables.clientIDs, answerIDs: [] },
            ...oldData,
          ] satisfies OwnProduct[];
        });
        // The write above no-ops while the list is still loading, and an
        // in-flight fetch can overwrite it with a stale list; refetch to converge.
        await queryClient.invalidateQueries({ queryKey: [OWN_PRODUCTS_QUERY] });
      },
    }),
  );
}

export function useUpdateProduct(options?: MutationOptions<Product, EditProductRequest>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(editProductRequest) {
        const res = await request<Product>(
          'PUT',
          `/products/${editProductRequest.productID}`,
          editProductRequest,
        );
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [OWN_PRODUCTS_QUERY] });
      },
    }),
  );
}

export function useArchiveProduct(options?: MutationOptions<Product, string>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(productID) {
        const res = await request<Product>('POST', `/products/${productID}/archive`);
        return res.data;
      },
      onSuccess(data) {
        queryClient.setQueryData([OWN_PRODUCTS_QUERY], (oldData: OwnProduct[] | undefined) => {
          if (!oldData) return undefined;
          return oldData.map((product) =>
            product.id === data.id ? { ...product, ...data } : product,
          ) satisfies OwnProduct[];
        });
      },
    }),
  );
}

export function useSetSupplierAssociatedProducts(
  options?: MutationOptions<string[], SetSupplierAssociatedProductsRequest>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(req) {
        const res = await request<string[]>(
          'PUT',
          `/suppliers/${req.supplierID}/associated-products`,
          { productIDs: req.productIDs },
        );
        return res.data;
      },
      onSuccess(data, { supplierID }) {
        queryClient.setQueriesData(
          {
            queryKey: [SUPPLIER_ASSOCIATED_PRODUCT_IDS_QUERY, toRef(supplierID)],
          },
          () => data,
        );
      },
    }),
  );
}

export function useSetClientAssociatedProducts(
  options?: MutationOptions<string[], SetClientAssociatedProductsRequest>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(req) {
        const res = await request<string[]>('PUT', `/clients/${req.clientID}/associated-products`, {
          productIDs: req.productIDs,
        });
        return res.data;
      },
      onSuccess(data, { clientID }) {
        queryClient.setQueriesData(
          {
            queryKey: [CLIENT_ASSOCIATED_PRODUCT_IDS_QUERY, toRef(clientID)],
          },
          () => data,
        );
      },
    }),
  );
}
